import React from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, CartesianGrid } from 'recharts';
import Icon from '../Icon';
import { chartPalette, corridorPalette, tooltipContentStyle } from '../../utils/chartTheme';

const formatCurrency = (val) => {
  const n = Number(val) || 0;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
};

const Stat = ({ value, label, accent }) => (
  <div>
    <div className="stat-value" style={accent ? { color: accent } : undefined}>{value}</div>
    <div className="stat-label">{label}</div>
  </div>
);

const TransactionMonitor = ({ summary = {}, corridors = [], timeseries = [] }) => {
  const formatted = (timeseries || []).map((t) => ({
    ...t,
    time: new Date(t.bucket).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
  }));

  const total = summary.total_count || 0;
  const failureRate = total > 0 ? ((summary.failed_count / total) * 100).toFixed(1) : '0.0';
  const avgProcessingMin = ((summary.avg_processing_time_s || 0) / 60).toFixed(1);

  return (
    <>
      {/* Stats */}
      <div className="card">
        <div className="row row-gap-3 mb-3">
          <Icon name="clock" size={18} style={{ color: 'var(--teal)' }} />
          <div className="card-title" style={{ margin: 0 }}>Transaction Monitor</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--s4)' }}>
          <Stat value={total} label="Total transactions (24h)" />
          <Stat value={formatCurrency(summary.onramp_volume)} label="On-ramp volume (CAD)" accent="var(--teal)" />
          <Stat value={formatCurrency(summary.offramp_volume)} label="Off-ramp volume" accent="var(--warn)" />
          <Stat value={`${avgProcessingMin}m`} label="Avg processing time" />
          <Stat
            value={`${failureRate}%`}
            label="Failure rate"
            accent={Number(failureRate) > 5 ? 'var(--err)' : 'var(--ok)'}
          />
        </div>
      </div>

      {/* Volume area chart */}
      <div className="card" style={{ gridColumn: 'span 2' }}>
        <div className="card-title">Transaction volume — last 24h</div>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={formatted} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
            <defs>
              <linearGradient id="volumeGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={chartPalette.teal} stopOpacity={0.45} />
                <stop offset="100%" stopColor={chartPalette.teal} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={chartPalette.grid} vertical={false} />
            <XAxis dataKey="time" tick={{ fontSize: 10, fill: chartPalette.textMid }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: chartPalette.textMid }} axisLine={false} tickLine={false} tickFormatter={formatCurrency} />
            <Tooltip contentStyle={tooltipContentStyle} formatter={(val) => [formatCurrency(val), 'Volume']} />
            <Area type="monotone" dataKey="volume" stroke={chartPalette.teal} fill="url(#volumeGrad)" strokeWidth={2.4} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Corridor breakdown */}
      <div className="card">
        <div className="card-title">Corridor breakdown</div>
        {(!corridors || corridors.length === 0) ? (
          <div className="empty" style={{ padding: 'var(--s8) var(--s4)' }}>
            <div className="empty-icon"><Icon name="dashboard" size={22} /></div>
            <h3 style={{ fontSize: 14 }}>No corridor activity</h3>
            <p>Volume by corridor will appear once transactions land in the last 24h.</p>
          </div>
        ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={corridors} layout="vertical" margin={{ top: 4, right: 12, bottom: 0, left: 8 }}>
            <CartesianGrid stroke={chartPalette.grid} horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 10, fill: chartPalette.textMid }} axisLine={false} tickLine={false} tickFormatter={formatCurrency} />
            <YAxis type="category" dataKey="corridor" tick={{ fontSize: 11, fill: chartPalette.textMid }} axisLine={false} tickLine={false} width={84} />
            <Tooltip contentStyle={tooltipContentStyle} formatter={(val) => [formatCurrency(val), 'Volume']} />
            <Bar dataKey="volume" radius={[0, 8, 8, 0]}>
              {(corridors || []).map((entry, i) => (
                <Cell key={i} fill={corridorPalette[entry.corridor] || chartPalette.teal} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        )}
      </div>
    </>
  );
};

export default TransactionMonitor;
